// AttachmentPicker — the three ways people actually add a file from a phone:
// take a photo, pick from the photo library, or attach a document.
//
// Why three explicit buttons instead of one <input type="file">: a single
// input with `capture` jumps straight to the camera and HIDES the photo
// library on iOS/Android (the Sep 15, 2026 warranty-claim complaint), while a
// single input without `capture` behaves differently on every platform. Three
// inputs with explicit accept/capture make each action predictable everywhere.
//
// The "Take photo" button only renders on touch devices — on a desktop it
// would just open the same file dialog as the other two.

import { useMemo } from "react";
import { ACCEPT_ANY_ATTACHMENT, ACCEPT_IMAGES } from "../../lib/warrantyAttachments";

export type AttachmentSource = "camera" | "library" | "file";

interface Props {
  onFiles: (files: File[], source: AttachmentSource) => void;
  disabled?: boolean;
  /** Visual tone: staff = slate/teal, homeowner = orange (matches the portal CTAs). */
  tone?: "staff" | "homeowner";
  /** Show only the document button (e.g. when photos have their own control). */
  documentsOnly?: boolean;
  className?: string;
}

function hasTouch(): boolean {
  if (typeof navigator === "undefined") return false;
  return (navigator.maxTouchPoints ?? 0) > 0 || "ontouchstart" in window;
}

export default function AttachmentPicker({ onFiles, disabled = false, tone = "staff", documentsOnly = false, className = "" }: Props) {
  const touch = useMemo(hasTouch, []);
  const base =
    "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border transition select-none " +
    (disabled ? "opacity-50 cursor-not-allowed " : "cursor-pointer ");
  const style =
    tone === "homeowner"
      ? "bg-white text-bardin-ink border-orange-300 hover:bg-orange-50"
      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50";

  function handle(source: AttachmentSource) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const list = e.target.files;
      const files = list ? Array.from(list) : [];
      // Reset so picking the same file twice in a row still fires onChange.
      e.target.value = "";
      if (files.length > 0) onFiles(files, source);
    };
  }

  return (
    <div className={"flex flex-wrap gap-2 " + className}>
      {!documentsOnly && touch && (
        <label className={base + style} aria-disabled={disabled}>
          <span aria-hidden>📷</span> Take photo
          <input
            type="file"
            accept={ACCEPT_IMAGES}
            capture="environment"
            className="hidden"
            disabled={disabled}
            onChange={handle("camera")}
          />
        </label>
      )}
      {!documentsOnly && (
        <label className={base + style} aria-disabled={disabled}>
          <span aria-hidden>🖼️</span> {touch ? "Photo library" : "Choose photos"}
          <input
            type="file"
            accept={ACCEPT_IMAGES}
            multiple
            className="hidden"
            disabled={disabled}
            onChange={handle("library")}
          />
        </label>
      )}
      <label className={base + style} aria-disabled={disabled}>
        <span aria-hidden>📎</span> Add a file
        <input
          type="file"
          accept={ACCEPT_ANY_ATTACHMENT}
          multiple
          className="hidden"
          disabled={disabled}
          onChange={handle("file")}
        />
      </label>
    </div>
  );
}
